import type { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';

import type { ConnectionDto } from '../generated/contracts';
import { ArrowModes, LineStyles, type ArrowMode, type LineStyle } from '../net/wire';
import { ConnectionRenderer } from '../room/ConnectionRenderer';
import { BIN_COLOR } from './TrashCanRenderer';

/**
 * A live preview of the connection style Link mode currently has selected, built at the tool
 * prop's own muzzle — the gap plan/mode-hierarchy.md names directly: "Link puts nothing [in the
 * dominant hand] either, and that is a gap, not a decision." Every other mode's held prop shows
 * *what pressing the trigger does* by what is visibly in the hand; Link had nothing, so the only
 * way to know what a drag would draw was the off-hand carousel (PR #153), reachable only while
 * the off-hand grip is held — easy to miss, and the one the brief that requested this class
 * names as the concrete cost: the position that *deletes an existing connection* was carried
 * entirely by a readout nobody was necessarily looking at, in a product with no undo.
 *
 * **Reuses `ConnectionRenderer`'s own construction rather than a second, parallel arrowhead/dash
 * implementation.** Twelve style combinations exist (four line styles including the UI-only
 * `'remove'`, times three arrow modes) precisely because none of it is authored geometry — it is
 * all built from `ConnectionStyleDto` the same way a real edge is. Building a second preview-only
 * renderer would either reimplement `ConnectionRenderer.buildStripe`'s dash/dot texture and
 * `placeArrow`'s cone geometry a second time (a maintenance seam this class exists to avoid: the
 * preview could silently drift from what a real edge actually looks like) or cover only a subset
 * of the twelve. This class owns one private `ConnectionRenderer` instance and calls its public
 * `render`/`remove` exactly the way `RoomView` does for real edges — the *only* difference is
 * where the two endpoints come from (a short stub ahead of the prop's muzzle, not two node
 * centres) and that the synthetic `ConnectionDto` this class builds is never sent to the hub or
 * stored anywhere.
 *
 * **Coordinate space.** `ConnectionRenderer`'s real, in-room usage parents its tube/arrow meshes
 * to `RoomView`'s own `roomOrigin` (`RoomView.ts`), but `roomOrigin` is constructed once, at
 * identity, and never repositioned or rotated anywhere in this codebase (`SceneRoot.ts`:
 * `new TransformNode('roomOrigin', this.scene)`, with nothing downstream ever writing its
 * `position`/`rotationQuaternion`/`scaling`) — the identical assumption `ConnectionDrawer`'s own
 * unparented drag-preview mesh already leans on (`this.preview = CreateCylinder(...)`, no
 * `.parent` assignment, positioned straight from `RoomView.meshFor(id).position`). This class
 * follows the same precedent: its own `ConnectionRenderer` is parented to a private, freshly
 * constructed `TransformNode` that is never itself parented or moved, so room space and this
 * class's own world-space `muzzle`/`forward` inputs (the same space `Grabber.aimOrigin`/
 * `forward` and `ToolPropRenderer`'s own unparented prop mesh already use) agree without this
 * class needing a dependency on `RoomView` or `SceneRoot` at all — both of which sit outside this
 * task's scope (`src/client-web/src/possession/`, `ConnectionRenderer.ts`).
 *
 * **The `'remove'` position must be unmistakable.** `ToolMode.linkLineSelection` can be a real
 * `LineStyle` or the UI-only `'remove'` value (plan/mode-hierarchy.md: re-linking an
 * already-connected pair with the style cycled to "remove" deletes the connection instead of
 * restyling it — there is no separate delete gesture, and no undo). `'remove'` is not itself a
 * wire `LineStyle` (deliberately — see `ConnectionStyleDto.cs`'s own remarks, mirrored in
 * `ToolMode.ts`), so this class renders the stub as an ordinary solid, arrow-less edge but
 * recolours it to `TrashCanRenderer.BIN_COLOR` — this product's one, established "this destroys
 * something" colour (the off-hand trash can, and PR #153's carousel `danger` row) — rather than
 * inventing a second red or a different shape. Every other line style renders with `color: null`,
 * the identical "no colour chosen" value a real `createConnection` call sends when Link mode has
 * nothing else to say about colour (`ConnectionDrawer.submit`), so the preview shows exactly the
 * grey `ConnectionRenderer.DEFAULT_COLOR` a freshly drawn edge would actually get — not a
 * preview-only colour that would make the stub look different from what dragging would produce.
 */
export class LinkPreviewRenderer {
  private readonly root: TransformNode;
  private readonly renderer: ConnectionRenderer;
  private shown = false;

  constructor(scene: Scene) {
    this.root = new TransformNode('link-preview-root', scene);
    this.renderer = new ConnectionRenderer(scene, this.root);
  }

  /**
   * Builds or updates the stub so it runs from `muzzle` forward along `direction` for
   * `PREVIEW_LENGTH`, styled per `lineSelection`/`arrowMode` — read fresh every call, the same
   * "no cached value that can go stale" rule every other per-tick readout in this project follows
   * (`ModeDefinition.ts`'s own `readoutLines`/`readoutSwatches`/`readoutOptionRows`). Safe to call
   * every tick while Link mode is active: `ConnectionRenderer.render` already reuses the existing
   * tube/material/dash-texture and arrow meshes rather than rebuilding them from scratch on a
   * call where nothing changed (its own `paint`'s `line !== edge.lineStyle` guard, `placeArrow`'s
   * enable/disable-not-recreate), so this is exactly as cheap as leaving a real edge's own render
   * call running every frame already is.
   *
   * `arrowMode` is ignored, and no arrowhead is drawn, whenever `lineSelection` is `'remove'` —
   * `ToolMode.linkArrowMode` is meaningless in that state (`ToolMode.linkArrowLabel` itself reads
   * `'n/a'` there), so showing whatever direction was last dialled in would draw a directional
   * arrow on a preview of an act that has no direction: deleting a connection.
   */
  render(muzzle: Vector3, direction: Vector3, lineSelection: LineStyle | 'remove', arrowMode: ArrowMode): void {
    const to = muzzle.add(direction.scale(PREVIEW_LENGTH));
    const isRemove = lineSelection === 'remove';

    const connection: ConnectionDto = {
      id: PREVIEW_CONNECTION_ID,
      orgId: PREVIEW_PLACEHOLDER_ID,
      diagramId: PREVIEW_PLACEHOLDER_ID,
      fromNodeId: PREVIEW_PLACEHOLDER_ID,
      toNodeId: PREVIEW_PLACEHOLDER_ID,
      label: null,
      style: {
        color: isRemove ? BIN_COLOR : null,
        line: isRemove ? LineStyles.Solid : lineSelection,
        arrow: isRemove ? ArrowModes.None : arrowMode,
      },
    };

    this.renderer.render(connection, muzzle, to);
    this.shown = true;
  }

  /**
   * Whether the stub is currently built — exposed for tests, which cannot see a rendered pixel
   * (CLAUDE.md, "When nothing in the project can see a rendered pixel").
   */
  get isShown(): boolean {
    return this.shown;
  }

  /**
   * Removes the stub — `ConnectionRenderer.remove` disposes its dash texture, its tube's own
   * material, the tube mesh, and both arrowhead meshes (`ConnectionRenderer.remove`'s own body),
   * so this is also this class's entire answer to "the preview's materials are actually freed
   * when the mode changes away": there is no second, parallel disposal path to keep in step with
   * `ConnectionRenderer`'s own, because this class never builds anything `ConnectionRenderer`
   * does not already know how to tear down. A no-op once nothing is shown, the same "only act on
   * a change" shape `TrashCanRenderer.setShown`/`ToolPropRenderer.syncControllerVisibility`
   * already use, so a caller does not have to track shown/hidden separately before calling this.
   */
  hide(): void {
    if (!this.shown) {
      return;
    }

    this.renderer.remove(PREVIEW_CONNECTION_ID);
    this.shown = false;
  }

  /** Drops whatever is shown and releases this class's own private root node. Used on teardown. */
  dispose(): void {
    this.hide();
    this.root.dispose();
  }
}

/**
 * How far ahead of the muzzle the stub reaches, in metres — short and deliberately so: this is a
 * sample of the style, not a preview of where an actual drag would land (which depends on the
 * aim ray and the node under it, `ConnectionDrawer`'s own concern, out of this task's scope).
 * Roughly a hand's own reach ahead of the prop, matching the visual scale `ToolPropRenderer`'s
 * other held props already sit at.
 */
const PREVIEW_LENGTH = 0.16;

/** Fixed id `ConnectionRenderer` keys its stub edge under — reused every call so `render()`
 *  updates the same tube/arrow meshes in place rather than creating a new edge each tick, the
 *  same "one id, updated in place" shape a real connection's own id gets from `RoomState`. */
const PREVIEW_CONNECTION_ID = 'link-mode-style-preview';

/**
 * `ConnectionDto.orgId`/`roomId`/`fromNodeId`/`toNodeId` are never read by
 * `ConnectionRenderer.render` (only `id` and `style` are) — see that method's own body — so this
 * placeholder exists purely to satisfy the generated type's required `string` fields, not because
 * any of them means anything for a preview that is never sent to the hub or looked up by node id.
 */
const PREVIEW_PLACEHOLDER_ID = 'link-mode-style-preview';
